import React, { FC, useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { divIcon, MarkerCluster } from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { makeStyles, Grid } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { mapColor } from '../../../theme';
import { UserStatusPriority } from '../../../models/types';
import moment from 'moment';
import getFullName from '../../../utils/getFullName';
import { paths } from '../../Root/paths';
import Link from 'antd/lib/typography/Link';
import { Popup } from 'react-leaflet';
import Icon from '../../../components_/Icon';
import BatteryIcon from '../../../components_/Icon/icons/BatteryIcon';

const useStyles = makeStyles(() => ({
  point: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: mapColor.darkApple,
    width: 28,
    height: 28,
    fontWeight: 500,
    color: mapColor.white
  },
  pulse: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: mapColor.darkApple,
    width: 28,
    height: 28,
    fontWeight: 500,
    color: mapColor.white,
    opacity: 0.5,
    animation: '$pulseEffect 1s infinite reverse'
  },
  '@keyframes pulseEffect': {
    '0%': {
      transform: 'scale(1)'
    },
    '100%': {
      transform: 'scale(2)'
    }
  },
  count: {
    borderRadius: '50%',
    width: 16,
    height: 16
  }
}));

const UserMarkerGroup: FC = ({ children }) => {
  const classes = useStyles();
  const [activeCluster, setActiveCluster] = useState<any>();
  const [popupData, setPopupData] = useState<any[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (activeCluster) {
      const markers = activeCluster.markers.map(
        (marker: any) => marker.options.children.props.user
      );

      const sortedMarkers = markers.sort((a: any, b: any) => {
        // Сортировка по приоритету
        const priorityOrder: Record<UserStatusPriority, number> = {
          [UserStatusPriority.CRITICAL]: 1,
          [UserStatusPriority.FOCUSED]: 2,
          [UserStatusPriority.NORMAL]: 3,
          [UserStatusPriority.HIDDEN]: 4
        };

        const priorityA =
          a.trackInfo.states?.reduce((acc: number, state: { priority: UserStatusPriority }) => {
            return Math.min(acc, priorityOrder[state.priority] || Number.MAX_VALUE);
          }, Number.MAX_VALUE) || Number.MAX_VALUE;

        const priorityB =
          b.trackInfo.states?.reduce((acc: number, state: { priority: UserStatusPriority }) => {
            return Math.min(acc, priorityOrder[state.priority] || Number.MAX_VALUE);
          }, Number.MAX_VALUE) || Number.MAX_VALUE;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // Сортировка по онлайн-статусу
        if (a.trackInfo.online !== b.trackInfo.online) {
          return a.trackInfo.online ? -1 : 1;
        }

        // Сортировка по времени timeEnd
        const timeA = moment(a.trackInfo.lastPosition.timeEnd);
        const timeB = moment(b.trackInfo.lastPosition.timeEnd);

        return timeB.isBefore(timeA) ? -1 : timeB.isAfter(timeA) ? 1 : 0;
      });

      setPopupData(sortedMarkers);
    }
  }, [activeCluster]);

  const handleClusterMouseClick = (cluster: any) => {
    const markers = cluster.layer.getAllChildMarkers();
    setActiveCluster({
      latlng: cluster.latlng,
      markers
    });
    setIsPopupOpen(true);
  };

  const handleClusterMouseOut = () => {
    // setActiveCluster(null);
  };

  const handleLinkClick = (id: string) => {
    setIsPopupOpen(false);
    history.push(paths.map.office.users.show(id));
  };

  const renderIcon = (count: number, isStates: UserStatusPriority) => (
    <>
      <Grid
        className={isStates === UserStatusPriority.FOCUSED ? classes.pulse : undefined}
        style={
          isStates === UserStatusPriority.FOCUSED
            ? { backgroundColor: mapColor.mtsRed }
            : isStates === UserStatusPriority.NORMAL
            ? { backgroundColor: mapColor.lightBanana }
            : { backgroundColor: mapColor.darkApple }
        }
      />
      <Grid
        container
        justify="center"
        alignItems="center"
        className={classes.point}
        style={
          isStates === UserStatusPriority.FOCUSED
            ? { backgroundColor: mapColor.mtsRed }
            : isStates === UserStatusPriority.NORMAL
            ? { backgroundColor: mapColor.lightBanana }
            : { backgroundColor: mapColor.darkApple }
        }
      >
        <Grid item container justify="center" alignItems="center" className={classes.count}>
          {count}
        </Grid>
      </Grid>
    </>
  );

  const getHighestPriority = (priorities: any[]): UserStatusPriority => {
    let highestPriority: UserStatusPriority = UserStatusPriority.HIDDEN;
    for (const status of priorities) {
      if (
        status.options.children.props.user.trackInfo.states &&
        status.options.children.props.user.trackInfo.states.some(
          (child: { priority: UserStatusPriority }) => child.priority === UserStatusPriority.FOCUSED
        )
      ) {
        highestPriority = UserStatusPriority.FOCUSED;
        break;
      } else if (
        status.options.children.props.user.trackInfo.states &&
        status.options.children.props.user.trackInfo.states.some(
          (child: { priority: UserStatusPriority }) => child.priority === UserStatusPriority.NORMAL
        )
      ) {
        highestPriority = UserStatusPriority.NORMAL;
        break;
      } else {
        highestPriority = UserStatusPriority.HIDDEN;
      }
    }
    return highestPriority;
  };

  const icon = (cluster: MarkerCluster) => {
    const markers = cluster.getAllChildMarkers();
    const isStates = getHighestPriority(markers);
    const count = cluster.getChildCount();

    return divIcon({
      iconSize: undefined,
      html: renderToString(renderIcon(count, isStates))
    });
  };

  return (
    <>
      <MarkerClusterGroup
        onclusterclick={handleClusterMouseClick}
        animateAddingMarkers
        animate
        spiderfyOnMaxZoom={false}
        zoomToBoundsOnClick={false}
        maxClusterRadius={20}
        iconCreateFunction={icon}
      >
        {children}
      </MarkerClusterGroup>
      {isPopupOpen && activeCluster && (
        <Popup
          position={activeCluster.latlng}
          onClose={() => setIsPopupOpen(false)}
          autoPan={false}
          offset={[15, 5]}
          // closeButton={false}
          minWidth={440}
          maxWidth={560}
          maxHeight={280}
        >
          <div>
            <table style={{ width: '100%' }}>
              <tbody>
                {popupData.map((user, index) => (
                  <tr key={index}>
                    <td style={{ verticalAlign: 'middle' }}>
                      {user.trackInfo &&
                      user.trackInfo.states &&
                      user.trackInfo.states.some(
                        (child: { priority: UserStatusPriority }) =>
                          child.priority === UserStatusPriority.CRITICAL ||
                          child.priority === UserStatusPriority.FOCUSED
                      ) ? (
                        <Icon name="error" size="s" />
                      ) : user.trackInfo && user.trackInfo.online ? (
                        <Icon name="checkMark" size="s" />
                      ) : (
                        <Icon name="errorGray" size="s" />
                      )}
                    </td>
                    <td>
                      <Link
                        onClick={e => {
                          e.preventDefault();
                          handleLinkClick(user.id);
                        }}
                      >
                        {getFullName(user, true)}
                      </Link>
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const measurements = (user.trackInfo && user.trackInfo.measurements) || [];
                        const chargeMeas = measurements.find((m: any) => m.type === 'DEVICE_CHARGE');
                        const isChargingMeas = measurements.find((m: any) => m.type === 'DEVICE_IS_CHARGING');
                        const raw =
                          (user.charge && (user.charge.percent as any)) ??
                          (user.trackInfo && user.trackInfo.charge && (user.trackInfo.charge.percent as any)) ??
                          (chargeMeas && chargeMeas.value);
                        const percent = Number(raw);
                        const isCharging =
                          (user.charge && user.charge.isCharging) ??
                          (isChargingMeas && String(isChargingMeas.value).toLowerCase() === 'true');
                        const isOnline = Boolean(user.trackInfo && user.trackInfo.online);
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {isOnline ? (
                              <BatteryIcon charge={Number.isFinite(percent) ? percent : 0} />
                            ) : (
                              <Icon name="battery" size="s" color="constant-greyscale-400" />
                            )}
                            <span style={{ fontSize: 12 }}>
                              {Number.isFinite(percent) ? percent : '--'}%
                            </span>
                            {isCharging ? <Icon name="charging" size="s" /> : null}
                          </span>
                        );
                      })()}
                    </td>
                    <td
                      style={{
                        verticalAlign: 'middle',
                        textAlign: 'right',
                        fontSize: '12px',
                        color: '#626C77'
                      }}
                    >
                      {moment(user.trackInfo.lastPosition.timeEnd).format('DD.MM.YYYY HH:mm:ss')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Popup>
      )}
    </>
  );
};

export default UserMarkerGroup;
